import { Dashboard } from "@/components/dashboard/Dashboard/Dashboard";
import { VStack } from "@/components/layout/VStack";
import { useEffect, useState } from "react";
import type { ApiUser } from "@shared/types/models";
import { useUsers } from "../hooks/users/useUsers";
import { UsersTable } from "../components/users/UsersDataTable";
import { EditUserSheet } from "../components/users/EditUserSheet";
import { useAuth } from "@/context/authContext";

const Users = () => {
  const { state } = useAuth();
  const [users, setUsers] = useState<ApiUser[] | null>(null);
  const [editing, setEditing] = useState<{
    open: boolean;
    user: ApiUser | null;
  }>({ open: false, user: null });

  const { loading, loadAllUsers, updateUser, deleteUser } = useUsers();

  useEffect(() => {
    loadAllUsers(setUsers);
  }, []);

  const onEdit = (userId: string) => {
    const user = users?.find((u) => u._id === userId) ?? null;
    if (!user) return;
    setEditing({ open: true, user });
  };

  const closeSheet = () => {
    setEditing((prev) => ({ ...prev, open: false }));
  };

  const onSave = async (patch: { name: string; gradYear: number | null }) => {
    if (!editing.user) return;
    const updated = await updateUser(editing.user._id, patch);
    if (!updated) return;
    setUsers((prev) =>
      prev ? prev.map((u) => (u._id === updated._id ? updated : u)) : prev,
    );
    closeSheet();
  };

  const onDelete = async () => {
    if (!editing.user) return;
    const ok = await deleteUser(editing.user._id);
    if (!ok) return;
    setUsers((prev) =>
      prev ? prev.filter((u) => u._id !== editing.user!._id) : prev,
    );
    closeSheet();
  };

  return (
    <Dashboard activePage="/users">
      <VStack>
        <h2
          style={{
            fontSize: "var(--text-2xl)",
            lineHeight: "var(--line-height-2xl)",
            fontWeight: "var(--font-bold)",
          }}
        >
          Users
        </h2>
        <p style={{ color: "var(--muted-foreground)" }}>
          Edit user details or remove users from the system.
        </p>
      </VStack>
      {loading.users && !users ? (
        <p style={{ color: "var(--muted-foreground)" }}>Loading users...</p>
      ) : (
        <UsersTable data={users ?? []} onEdit={onEdit} />
      )}
      {editing.user && (
        <EditUserSheet
          user={editing.user}
          open={editing.open}
          onOpenChange={(open) =>
            setEditing((prev) => ({ ...prev, open }))
          }
          isSelf={state.user?._id === editing.user._id}
          saving={loading.saving}
          deleting={loading.deleting}
          onSave={onSave}
          onDelete={onDelete}
        />
      )}
    </Dashboard>
  );
};

export default Users;
